import {validateGraphConfig, validateGraphPayload} from "./graphValidationHelpers";
import BaseToolEngine from "@shared/GenericTools/engines/BaseToolEngine";
import type {
	EnginePayload,
	GraphEdgeEngineInterface,
	GraphNodeEngineInterface,
	GraphPlacementEngineInterface,
	GraphSchemaEngineInterface,
	ToolConfig,
} from "@shared/GenericTools/EngineInterfaces/ToolEngineInterfaceRegistry";
import {
	GraphEdgeEngineInterfaceToken,
	GraphNodeEngineInterfaceToken,
	GraphPlacementEngineInterfaceToken,
	GraphSchemaEngineInterfaceToken,
} from "@shared/GenericTools/EngineInterfaces/ToolEngineInterfaceRegistry";
import {
	ToolEngineValidationResult,
	type ToolEngineDataShape,
	type ToolGraphEdgeData,
	type ToolGraphNodeData,
	type ToolResultSnapshotData,
	type ToolInstanceSnapshotData,
} from "@shared/GenericTools/EngineInterfaces/ToolEngineDataTypes";

const DEFAULT_NODE_TYPE = "default"
const DEFAULT_EDGE_TYPE = "default"
const DEFAULT_VIEWPORT = Object.freeze({
	pan: Object.freeze({x: 0, y: 0}),
	zoom: 1,
})

type GraphCollectionName = "nodes" | "edges"

export default class GraphToolEngine extends BaseToolEngine implements
	GraphSchemaEngineInterface,
	GraphNodeEngineInterface,
	GraphEdgeEngineInterface,
	GraphPlacementEngineInterface {


	validateConfig(config: ToolConfig = {}): ToolEngineValidationResult {
		return this.collectWorkValidation(() => validateGraphConfig(config))
	}

	validateWorkPayload(payload: EnginePayload, config: ToolConfig): ToolEngineValidationResult {
		return this.collectWorkValidation(() => validateGraphPayload(payload, config))
	}

	getEngineInterfaceTokens() {
		return [
			GraphSchemaEngineInterfaceToken,
			GraphNodeEngineInterfaceToken,
			GraphEdgeEngineInterfaceToken,
			GraphPlacementEngineInterfaceToken,
		]
	}

	createDefaultPayload(config: ToolConfig = {}): EnginePayload {
		return this.createGraphEnginePayload({}, this.createGraphConfigFromToolConfig(config))
	}

	normalizePayload(enginePayload: EnginePayload = {}, config: ToolConfig = {}): EnginePayload {
		return this.normalizeGraphPayload(enginePayload, this.createGraphConfigFromToolConfig(config))
	}

	createSummaryInput(enginePayload: EnginePayload = {}, config: ToolConfig = {}): EnginePayload {
		return this.normalizeGraphPayload(enginePayload, this.createGraphConfigFromToolConfig(config))
	}

	createGraphConfigFromToolConfig(config: ToolConfig = {}): ToolConfig {
		return {
			title: config.title || "",
			instructions: config.instructions || "",
			nodeTypes: this.cloneConfigArray(config.nodeTypes),
			edgeTypes: this.cloneConfigArray(config.edgeTypes),
			templates: this.cloneConfigArray(config.templates),
			graphTemplate: this.cloneConfig((config.graphTemplate as ToolConfig | undefined) || {}),
			defaultViewport: this.cloneConfig((config.defaultViewport as ToolConfig | undefined) || DEFAULT_VIEWPORT),
		}
	}

	createToolConfigFromGraphConfig(graphConfig: ToolConfig = {}): ToolConfig {
		return this.createGraphConfigFromToolConfig(graphConfig)
	}

	createGraphEnginePayload(enginePayload: EnginePayload = {}, graphConfig: ToolConfig = {}): EnginePayload {
		let graph = (enginePayload.graph as ToolConfig | undefined) || {}
		let rendererState = (enginePayload.rendererState as ToolConfig | undefined) || {}
		return this.normalizeGraphPayload({
			...enginePayload,
			graph: {
				nodes: this.cloneConfigArray(graph.nodes),
				edges: this.cloneConfigArray(graph.edges),
			},
			rendererState: {
				...rendererState,
				viewport: this.cloneConfig((rendererState.viewport as ToolConfig | undefined) || (graphConfig.defaultViewport as ToolConfig | undefined) || DEFAULT_VIEWPORT),
			},
		}, graphConfig)
	}

	normalizeGraphPayload(enginePayload: EnginePayload = {}, graphConfig: ToolConfig = {}): EnginePayload {
		let graph = (enginePayload.graph as ToolConfig | undefined) || {}
		let rendererState = (enginePayload.rendererState as ToolConfig | undefined) || {}
		return {
			...enginePayload,
			graph: {
				nodes: this.createGraphNodes(graph.nodes as ToolConfig[] | undefined),
				edges: this.createGraphEdges(graph.edges as ToolConfig[] | undefined),
			},
			rendererState: {
				...rendererState,
				viewport: this.createViewport(rendererState.viewport as ToolConfig | undefined, graphConfig),
			},
		}
	}

	validateGraphPayload(enginePayload: EnginePayload = {}, graphConfig: ToolConfig = {}): ToolEngineValidationResult {
		let normalizedPayload = this.normalizeGraphPayload(enginePayload, graphConfig)
		let result = new ToolEngineValidationResult()
		this.addDuplicateIdErrors(result, normalizedPayload)
		this.addEdgeReferenceErrors(result, normalizedPayload)
		result.isValid = result.errors.length === 0
		return result
	}

	createGraphResultConfig(toolResult: ToolResultSnapshotData | null = null, toolInstance: ToolInstanceSnapshotData | null = null): ToolConfig {
		let summary = (toolResult?.summary as ToolConfig | undefined) || {}
		let enginePayload = (toolResult?.enginePayload as ToolConfig | undefined) || {}
		return this.createGraphConfigFromToolConfig((summary.graphConfig as ToolConfig | undefined) || (enginePayload.graphConfig as ToolConfig | undefined) || toolInstance?.config || {})
	}

	createGraphNode(node: ToolConfig = {}, index = 0): ToolGraphNodeData {
		return {
			id: String(node.id || `node${index + 1}`),
			type: String(node.type || DEFAULT_NODE_TYPE),
			data: this.cloneConfig((node.data as ToolConfig | undefined) || {}),
		}
	}

	addGraphNode(enginePayload: EnginePayload = {}, node: ToolConfig = {}): EnginePayload {
		return this.upsertGraphCollectionItem(enginePayload, "nodes", this.createGraphNode(node, this.getGraphNodes(enginePayload).length))
	}

	updateGraphNode(enginePayload: EnginePayload = {}, nodeId = "", changes: ToolConfig = {}): EnginePayload {
		return this.updateGraphCollectionItem(enginePayload, "nodes", nodeId, changes)
	}

	deleteGraphNode(enginePayload: EnginePayload = {}, nodeId = ""): EnginePayload {
		let withoutNode = this.deleteGraphCollectionItem(enginePayload, "nodes", nodeId)
		return this.deleteGraphEdgesForNode(withoutNode, nodeId)
	}

	getGraphNodes(enginePayload: EnginePayload = {}): ToolGraphNodeData[] {
		return this.getGraphCollection(enginePayload, "nodes") as ToolGraphNodeData[]
	}

	findGraphNodeById(enginePayload: EnginePayload = {}, nodeId = ""): ToolGraphNodeData | null {
		return this.findGraphCollectionItemById(enginePayload, "nodes", nodeId) as ToolGraphNodeData | null
	}

	createGraphEdge(edge: ToolConfig = {}, index = 0): ToolGraphEdgeData {
		return {
			id: String(edge.id || `edge${index + 1}`),
			type: String(edge.type || DEFAULT_EDGE_TYPE),
			sourceId: String(edge.sourceId || ""),
			targetId: String(edge.targetId || ""),
			data: this.cloneConfig((edge.data as ToolConfig | undefined) || {}),
		}
	}

	addGraphEdge(enginePayload: EnginePayload = {}, edge: ToolConfig = {}): EnginePayload {
		return this.upsertGraphCollectionItem(enginePayload, "edges", this.createGraphEdge(edge, this.getGraphEdges(enginePayload).length))
	}

	updateGraphEdge(enginePayload: EnginePayload = {}, edgeId = "", changes: ToolConfig = {}): EnginePayload {
		return this.updateGraphCollectionItem(enginePayload, "edges", edgeId, changes)
	}

	deleteGraphEdge(enginePayload: EnginePayload = {}, edgeId = ""): EnginePayload {
		return this.deleteGraphCollectionItem(enginePayload, "edges", edgeId)
	}

	getGraphEdges(enginePayload: EnginePayload = {}): ToolGraphEdgeData[] {
		return this.getGraphCollection(enginePayload, "edges") as ToolGraphEdgeData[]
	}

	findGraphEdgeById(enginePayload: EnginePayload = {}, edgeId = ""): ToolGraphEdgeData | null {
		return this.findGraphCollectionItemById(enginePayload, "edges", edgeId) as ToolGraphEdgeData | null
	}

	updateGraphNodePosition(enginePayload: EnginePayload = {}, nodeId = "", position: ToolConfig = {}): EnginePayload {
		let node = this.findGraphNodeById(enginePayload, nodeId)
		return this.updateGraphNode(enginePayload, nodeId, {
			data: {
				...(node?.data || {}),
				position: this.createPosition(position),
			},
		})
	}

	updateGraphViewport(enginePayload: EnginePayload = {}, viewport: ToolConfig = {}): EnginePayload {
		let normalizedPayload = this.normalizeGraphPayload(enginePayload)
		return {
			...normalizedPayload,
			rendererState: {
				...(normalizedPayload.rendererState as ToolConfig || {}),
				viewport: this.createViewport(viewport),
			},
		}
	}

	getGraphViewport(enginePayload: EnginePayload = {}): ToolConfig {
		let normalizedPayload = this.normalizeGraphPayload(enginePayload)
		let rendererState = (normalizedPayload.rendererState as ToolConfig | undefined) || {}
		return this.cloneConfig((rendererState.viewport as ToolConfig | undefined) || DEFAULT_VIEWPORT)
	}

	createGraphNodes(nodes: ToolConfig[] = []): ToolGraphNodeData[] {
		return this.cloneConfigArray(nodes).map((node, index) => this.createGraphNode(node, index))
	}

	createGraphEdges(edges: ToolConfig[] = []): ToolGraphEdgeData[] {
		return this.cloneConfigArray(edges).map((edge, index) => this.createGraphEdge(edge, index))
	}

	getGraphCollection(enginePayload: EnginePayload = {}, collectionName: GraphCollectionName): ToolConfig[] {
		let normalizedPayload = this.normalizeGraphPayloadShallow(enginePayload)
		return this.cloneConfigArray((normalizedPayload.graph as ToolConfig)[collectionName] as ToolConfig[] | undefined)
	}

	findGraphCollectionItemById(enginePayload: EnginePayload = {}, collectionName: GraphCollectionName, itemId = ""): ToolConfig | null {
		return this.getGraphCollection(enginePayload, collectionName).find(itemLocal => itemLocal.id === itemId) || null
	}

	upsertGraphCollectionItem(enginePayload: EnginePayload = {}, collectionName: GraphCollectionName, item: ToolConfig = {}): EnginePayload {
		let normalizedPayload = this.normalizeGraphPayload(enginePayload)
		let graph = normalizedPayload.graph as ToolConfig
		let collection = this.cloneConfigArray(graph[collectionName] as ToolConfig[] | undefined)
		let existingIndex = collection.findIndex(itemLocal => itemLocal.id === item.id)
		let nextCollection = existingIndex >= 0
			? collection.map((itemLocal, index) => index === existingIndex ? item : itemLocal)
			: [...collection, item]
		return this.updateGraphCollection(normalizedPayload, collectionName, nextCollection)
	}

	updateGraphCollectionItem(enginePayload: EnginePayload = {}, collectionName: GraphCollectionName, itemId = "", changes: ToolConfig = {}): EnginePayload {
		let normalizedPayload = this.normalizeGraphPayload(enginePayload)
		let graph = normalizedPayload.graph as ToolConfig
		let collection = this.cloneConfigArray(graph[collectionName] as ToolConfig[] | undefined)
		let nextCollection = collection.map(item => item.id === itemId ? {...item, ...this.cloneConfig(changes)} : item)
		return this.updateGraphCollection(normalizedPayload, collectionName, nextCollection)
	}

	deleteGraphCollectionItem(enginePayload: EnginePayload = {}, collectionName: GraphCollectionName, itemId = ""): EnginePayload {
		let normalizedPayload = this.normalizeGraphPayload(enginePayload)
		let graph = normalizedPayload.graph as ToolConfig
		let collection = this.cloneConfigArray(graph[collectionName] as ToolConfig[] | undefined)
		return this.updateGraphCollection(normalizedPayload, collectionName, collection.filter(item => item.id !== itemId))
	}

	updateGraphCollection(enginePayload: EnginePayload = {}, collectionName: GraphCollectionName, collection: ToolConfig[] = []): EnginePayload {
		let normalizedPayload = this.normalizeGraphPayloadShallow(enginePayload)
		let graph = normalizedPayload.graph as ToolConfig
		return {
			...normalizedPayload,
			graph: {
				...graph,
				[collectionName]: collection,
			},
		}
	}

	deleteGraphEdgesForNode(enginePayload: EnginePayload = {}, nodeId = ""): EnginePayload {
		let normalizedPayload = this.normalizeGraphPayload(enginePayload)
		let edges = this.getGraphEdges(normalizedPayload).filter(edge => edge.sourceId !== nodeId && edge.targetId !== nodeId)
		return this.updateGraphCollection(normalizedPayload, "edges", edges)
	}

	normalizeGraphPayloadShallow(enginePayload: EnginePayload = {}): EnginePayload {
		let graph = (enginePayload.graph as ToolConfig | undefined) || {}
		let rendererState = (enginePayload.rendererState as ToolConfig | undefined) || {}
		return {
			...enginePayload,
			graph: {
				nodes: this.cloneConfigArray(graph.nodes as ToolConfig[] | undefined),
				edges: this.cloneConfigArray(graph.edges as ToolConfig[] | undefined),
			},
			rendererState: this.cloneConfig(rendererState),
		}
	}

	createPosition(position: ToolConfig = {}): ToolEngineDataShape {
		return {
			x: Number(position.x) || 0,
			y: Number(position.y) || 0,
		}
	}

	createViewport(viewport: ToolConfig = {}, graphConfig: ToolConfig = {}): ToolConfig {
		let sourceViewport = viewport || graphConfig.defaultViewport || DEFAULT_VIEWPORT
		let pan = (sourceViewport.pan as ToolConfig | undefined) || {}
		return {
			pan: this.createPosition(pan),
			zoom: Number(sourceViewport.zoom) || 1,
		}
	}

	addDuplicateIdErrors(validationResult: ToolEngineValidationResult, enginePayload: EnginePayload = {}): void {
		for (let collectionName of ["nodes", "edges"] as GraphCollectionName[]) {
			let duplicateIds = this.getDuplicateIds(this.getGraphCollection(enginePayload, collectionName))
			for (let duplicateId of duplicateIds) {
				validationResult.errors.push(`Duplicate graph ${collectionName} id: ${duplicateId}`)
			}
		}
	}

	addEdgeReferenceErrors(validationResult: ToolEngineValidationResult, enginePayload: EnginePayload = {}): void {
		let nodeIds = new Set(this.getGraphNodes(enginePayload).map(node => String(node.id || "")))
		for (let edge of this.getGraphEdges(enginePayload)) {
			if (!nodeIds.has(String(edge.sourceId || ""))) {
				validationResult.errors.push(`Graph edge has unknown source node: ${edge.id}`)
			}
			if (edge.targetId && !nodeIds.has(String(edge.targetId || ""))) {
				validationResult.errors.push(`Graph edge has unknown target node: ${edge.id}`)
			}
		}
	}

	getDuplicateIds(items: ToolConfig[] = []): string[] {
		let seenIds = new Set<string>()
		let duplicateIds = new Set<string>()
		for (let item of items) {
			let itemId = String(item.id || "")
			if (seenIds.has(itemId)) {
				duplicateIds.add(itemId)
			}
			seenIds.add(itemId)
		}
		return Array.from(duplicateIds)
	}

	cloneConfig(config: unknown = {}): ToolConfig {
		return JSON.parse(JSON.stringify(config || {}))
	}

	cloneConfigArray(items: unknown): ToolConfig[] {
		return Array.isArray(items) ? JSON.parse(JSON.stringify(items)) : []
	}
}
